// Outside publishers: who hosts our apps in their Play consoles, what we owe
// them each week, and how each app is doing (handed over → in the store →
// listing matches the package).
//
// Money is computed, never typed: a publish fee is the "published" event the
// store poll wrote, rent is the app's weekly rate pro-rated by the days it was
// live in the Mon–Sun week. Rates are per app and dated: a new rent applies
// from a chosen week on, so the weeks already paid never move. Only the
// week's status (open / agreed / paid) and what was actually paid are manual.
import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import { useThemeStore } from '@/store/theme';
import { useThemeColors } from '@/hooks/useThemeColors';
import {
  createPublisher, getPublisherApps, setPublisherAppAccount, getPublisherEconomics, getPublisherWeek, getPublishers, runPublisherListingCheck, approvePublisherApp, revokePublisherApproval,
  setPublisherRent, testPublisherMessage, updatePublisher, updatePublisherWeek,
  type AppEconomics, type ListingReport, type LocaleIssue, type Publisher, type PublisherApp, type PublisherInput,
  type PublisherWeekRow, type WeekStatus,
} from '@/api/publishers';

// ---------------------------------------------------------------- helpers

function money(cents: number | null | undefined): string {
  if (cents == null) return '—';
  const abs = Math.abs(cents);
  const s = `$${Math.floor(abs / 100)}${abs % 100 ? '.' + String(abs % 100).padStart(2, '0') : ''}`;
  return cents < 0 ? `-${s}` : s;
}
const usd = (cents: number | null | undefined) => cents == null ? '' : String(cents / 100);
const toCents = (v: string) => Math.round(Number(v || 0) * 100);

function mondayOf(d: Date): string {
  const back = (d.getUTCDay() + 6) % 7;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - back)).toISOString().slice(0, 10);
}
function shiftWeek(week: string, n: number): string {
  const d = new Date(`${week}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 7 * n);
  return d.toISOString().slice(0, 10);
}
function weekLabel(week: string): string {
  const a = new Date(`${week}T00:00:00Z`);
  const b = new Date(a.getTime() + 6 * 86400_000);
  const f = (d: Date) => d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${f(a)} – ${f(b)}`;
}
const fmtTs = (s: string | null | undefined) => (s ? s.slice(0, 16) : '—');
const fmtD = (s: string) => s.slice(5, 10).split('-').reverse().join('.');
const fmtUnixDay = (s: number) => fmtD(new Date(s * 1000).toISOString().slice(0, 10));
const days = (n: number) => (n % 10 === 1 && n !== 11 ? 'день' : n % 10 >= 2 && n % 10 <= 4 && (n < 10 || n > 20) ? 'дня' : 'дней');

const WEEK_STATUS: { value: WeekStatus; label: string; tone: string }[] = [
  { value: 'open', label: 'Открыто', tone: 'bg-surface-muted text-content-muted' },
  { value: 'agreed', label: 'Согласовано', tone: 'bg-primary/10 text-primary' },
  { value: 'paid', label: 'Оплачено', tone: 'bg-success/10 text-success' },
];

const ISSUE_LABEL: Record<LocaleIssue, string> = {
  not_available: 'страница 404',
  fetch_error: 'не проверилось',
  locale_missing: 'локаль не залита (стор показывает английский)',
  title_mismatch: 'название не совпадает',
  short_mismatch: 'короткое описание не совпадает',
  screenshot_count: 'скринов не столько',
};
const LISTING_LABEL: Record<string, { label: string; tone: string }> = {
  pending: { label: 'ждёт проверки', tone: 'bg-surface-muted text-content-muted' },
  ok: { label: 'листинг ок', tone: 'bg-success/10 text-success' },
  issues: { label: 'есть расхождения', tone: 'bg-warning/10 text-warning' },
  gave_up: { label: 'не сошлось, проверки остановлены', tone: 'bg-danger/10 text-danger' },
};

function Pill({ tone, children }: { tone: string; children: React.ReactNode }) {
  return <span className={`inline-block rounded px-2 py-0.5 text-xs ${tone}`}>{children}</span>;
}

/** The outside client an app was published for; nothing for ours. */
function ClientTag({ name }: { name: string | null }) {
  if (!name) return null;
  return <Pill tone="ml-1 bg-primary/10 text-primary">{name}</Pill>;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs uppercase tracking-wide text-content-subtle">{label}</div>
      <div className="mt-0.5 text-base font-semibold text-content">{value}</div>
      {hint && <div className="text-xs text-content-subtle">{hint}</div>}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content';

// ---------------------------------------------------------------- publisher form

function PublisherDialog({ initial, onClose, onSaved }: {
  initial: Publisher | null; onClose: () => void; onSaved: () => void;
}) {
  const pushToast = useToastStore((s) => s.push);
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    chat_id: initial?.chat_id ?? '',
    thread_id: initial?.thread_id == null ? '' : String(initial.thread_id),
    publish: usd(initial?.publish_cents ?? 4000),
    rent: usd(initial?.rent_cents ?? 0),
    contact: initial?.contact ?? '',
    note: initial?.note ?? '',
    enabled: initial ? !!initial.enabled : true,
  });
  const set = (k: keyof typeof form, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const input: PublisherInput = {
        name: form.name.trim(),
        chat_id: form.chat_id.trim() || null,
        thread_id: form.thread_id.trim() ? Number(form.thread_id) : null,
        publish_cents: toCents(form.publish),
        rent_cents: toCents(form.rent),
        contact: form.contact.trim() || null,
        note: form.note.trim() || null,
        enabled: form.enabled,
      };
      return initial ? updatePublisher(initial.id, input) : createPublisher(input);
    },
    onSuccess: () => { pushToast(initial ? 'Публикатор обновлён' : 'Публикатор добавлен'); onSaved(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось сохранить'),
  });
  const test = useMutation({
    mutationFn: () => testPublisherMessage(initial!.id),
    onSuccess: () => pushToast('Сообщение ушло в чат'),
    onError: (e: any) => pushToast(e?.message || 'Не удалось отправить'),
  });

  return (
    <Modal isOpen onClose={onClose} title={initial ? initial.name : 'Новый публикатор'} size="lg">
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-content-muted mb-1">Название</label>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs text-content-muted mb-1">Telegram chat_id</label>
            <input value={form.chat_id} onChange={(e) => set('chat_id', e.target.value)} className={inputCls} placeholder="-1001234567890" />
            <p className="mt-1 text-xs text-content-subtle">Группа с этим публикатором, бот должен быть в ней.</p>
          </div>
          <div>
            <label className="block text-xs text-content-muted mb-1">Topic id (если форум)</label>
            <input value={form.thread_id} onChange={(e) => set('thread_id', e.target.value)} className={inputCls} placeholder="необязательно" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs text-content-muted mb-1">$ за публикацию (по умолчанию)</label>
            <input type="number" min="0" value={form.publish} onChange={(e) => set('publish', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-content-muted mb-1">$ аренда в неделю за приложение</label>
            <input type="number" min="0" value={form.rent} onChange={(e) => set('rent', e.target.value)} className={inputCls} />
          </div>
        </div>
        <p className="text-xs text-content-subtle">
          Это цены по умолчанию для новых выдач: при выдаче ссылки их можно поменять для конкретного приложения.
          Уже отданные приложения остаются на своих ценах; их аренда меняется в карточке приложений — «Изменить аренду».
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs text-content-muted mb-1">Контакт</label>
            <input value={form.contact} onChange={(e) => set('contact', e.target.value)} className={inputCls} placeholder="@username" />
          </div>
          <label className="flex items-end gap-2 pb-2 text-sm text-content">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Активен (виден в списке на вкладке Share)
          </label>
        </div>
        <div>
          <label className="block text-xs text-content-muted mb-1">Заметка</label>
          <textarea value={form.note} onChange={(e) => set('note', e.target.value)} className={inputCls} rows={2} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <div>
            {initial && (
              <Button variant="secondary" size="sm" loading={test.isPending} onClick={() => test.mutate()} disabled={!initial.chat_id}>
                Тест сообщения в чат
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>Отмена</Button>
            <Button loading={save.isPending} disabled={!form.name.trim()} onClick={() => save.mutate()}>Сохранить</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- listing report

function ReportView({ report }: { report: ListingReport }) {
  return (
    <div className="space-y-1 text-xs">
      <div className="text-content-subtle">Проверено {fmtTs(report.checked_at)}</div>
      {report.locales.map((l) => (
        <div key={l.lang} className="flex flex-wrap gap-x-2">
          <span className="font-mono text-content">{l.lang}</span>
          {l.ok
            ? <span className="text-success">ок</span>
            : l.issues.map((i) => (
              <span key={i} className="text-warning">
                {ISSUE_LABEL[i]}
                {i === 'screenshot_count' && ` (${l.store.screenshots ?? '?'} вместо ${l.expected.screenshots})`}
                {i === 'title_mismatch' && ` («${l.store.title ?? ''}» вместо «${l.expected.title}»)`}
              </span>
            ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- rent change

/**
 * New weekly rent for the apps the publisher already hosts. Dated by week:
 * the bill for weeks before it stays as it was, so a price cut agreed today
 * does not touch what was already paid. New handoffs take the publisher's
 * default, which can be updated in the same go.
 */
function RentDialog({ publisher, apps, onClose, onSaved }: {
  publisher: Publisher; apps: PublisherApp[]; onClose: () => void; onSaved: () => void;
}) {
  const pushToast = useToastStore((s) => s.push);
  const current = mondayOf(new Date());
  const [rent, setRent] = useState('');
  const [fromWeek, setFromWeek] = useState(current);
  const [setDefault, setSetDefault] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(apps.map((a) => a.id)));
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const weeks = [-1, 0, 1, 2].map((n) => shiftWeek(current, n));
  const save = useMutation({
    mutationFn: () => setPublisherRent(publisher.id, {
      rent_cents: toCents(rent), from_week: fromWeek, app_ids: [...picked], set_default: setDefault,
    }),
    onSuccess: (r) => { pushToast(`Аренда изменена у ${r.changed} прил.`); onSaved(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось сохранить'),
  });
  return (
    <Modal isOpen onClose={onClose} title={`${publisher.name} · изменить аренду`} size="2xl">
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs text-content-muted mb-1">$ аренда в неделю</label>
            <input type="number" min="0" step="1" value={rent} onChange={(e) => setRent(e.target.value)} className={inputCls} autoFocus />
          </div>
          <div>
            <label className="block text-xs text-content-muted mb-1">С какой недели</label>
            <select value={fromWeek} onChange={(e) => setFromWeek(e.target.value)} className={inputCls}>
              {weeks.map((w) => (
                <option key={w} value={w}>{weekLabel(w)}{w === current ? ' · текущая' : ''}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-xs text-content-subtle">
          Недели до выбранной считаются по прежней ставке — оплаченные счета не изменятся.
          Приложения, которые ещё не вышли, просто получают новую ставку.
        </p>
        <label className="flex items-center gap-2 text-sm text-content">
          <input type="checkbox" checked={setDefault} onChange={(e) => setSetDefault(e.target.checked)} />
          Сделать ставкой по умолчанию для новых выдач (сейчас {money(publisher.rent_cents)}/нед)
        </label>
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-content-muted">
            <span>Приложения · выбрано {picked.size} из {apps.length}</span>
            <span className="flex gap-2">
              <button type="button" className="text-primary hover:underline" onClick={() => setPicked(new Set(apps.map((a) => a.id)))}>все</button>
              <button type="button" className="text-primary hover:underline" onClick={() => setPicked(new Set())}>никого</button>
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-md border border-border">
            {apps.map((a) => (
              <label key={a.id} className="flex items-center gap-2 border-b border-border px-2 py-1.5 text-sm last:border-b-0">
                <input type="checkbox" checked={picked.has(a.id)} onChange={() => toggle(a.id)} />
                <span className="flex-1 truncate text-content">{a.name}</span>
                <span className="text-xs text-content-muted whitespace-nowrap">
                  {a.rent_cents ? `${money(a.rent_cents)}/нед` : 'без аренды'}
                  {a.rent_from_week ? ` с ${fmtD(a.rent_from_week)}` : ''}
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} disabled={rent.trim() === '' || picked.size === 0} onClick={() => save.mutate()}>
            Применить
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- apps of one publisher

function PublisherApps({ publisher, onClose }: { publisher: Publisher; onClose: () => void }) {
  const canEdit = useCanEdit();
  const pushToast = useToastStore((s) => s.push);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['publisher-apps', publisher.id],
    queryFn: () => getPublisherApps(publisher.id),
  });
  const [open, setOpen] = useState<string | null>(null);
  const [rentOpen, setRentOpen] = useState(false);
  const check = useMutation({
    mutationFn: (appId: string) => runPublisherListingCheck(appId),
    onSuccess: (r) => {
      pushToast(r.ok ? 'Листинг совпадает с пакетом' : 'Есть расхождения, отчёт ниже');
      qc.invalidateQueries({ queryKey: ['publisher-apps', publisher.id] });
    },
    onError: (e: any) => pushToast(e?.message || 'Проверка не удалась'),
  });
  const approval = useMutation({
    mutationFn: (v: { appId: string; approve: boolean }) => (v.approve ? approvePublisherApp(v.appId) : revokePublisherApproval(v.appId)),
    onSuccess: (_d, v) => {
      pushToast(v.approve ? 'Апрувнуто — платим с выхода' : 'Апрув снят — не платим до нового апрува');
      qc.invalidateQueries({ queryKey: ['publisher-apps', publisher.id] });
      qc.invalidateQueries({ queryKey: ['publisher-week'] });
      qc.invalidateQueries({ queryKey: ['publisher-economics'] });
    },
    onError: (e: any) => pushToast(e?.message || 'Не сохранилось'),
  });
  const account = useMutation({
    mutationFn: (v: { appId: string; account: string | null }) => setPublisherAppAccount(v.appId, v.account),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['publisher-apps', publisher.id] }),
    onError: (e: any) => pushToast(e?.message || 'Не сохранилось'),
  });
  function editAccount(a: PublisherApp) {
    const v = window.prompt(
      `Аккаунт разработчика для «${a.name}»${a.store_developer ? ` (в сторе: ${a.store_developer})` : ''}. Пусто — брать из стора.`,
      a.publisher_account ?? a.store_developer ?? '',
    );
    if (v === null) return;
    account.mutate({ appId: a.id, account: v.trim() || null });
  }

  const rows = data ?? [];
  return (
    <Modal isOpen onClose={onClose} title={`${publisher.name} · приложения`} size="4xl">
      {isLoading && <p className="text-sm text-content-subtle">Загрузка…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="text-sm text-content-subtle">Пока ничего не отдавали. Публикатор выбирается на вкладке Share при создании ссылки.</p>
      )}
      {rows.length > 0 && canEdit && (
        <div className="mb-2 flex justify-end">
          <Button variant="secondary" size="sm" onClick={() => setRentOpen(true)}>Изменить аренду</Button>
        </div>
      )}
      {rentOpen && (
        <RentDialog
          publisher={publisher}
          apps={rows}
          onClose={() => setRentOpen(false)}
          onSaved={() => {
            setRentOpen(false);
            qc.invalidateQueries({ queryKey: ['publisher-apps', publisher.id] });
            qc.invalidateQueries({ queryKey: ['publishers'] });
            qc.invalidateQueries({ queryKey: ['publisher-week'] });
            qc.invalidateQueries({ queryKey: ['publisher-economics'] });
          }}
        />
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle">
                <th className="py-1 pr-2 font-normal">Приложение</th>
                <th className="py-1 pr-2 font-normal" title="Внешний клиент, для которого опубликовано; пусто — наше">Клиент</th>
                <th className="py-1 pr-2 font-normal">Отдано</th>
                <th className="py-1 pr-2 font-normal">В сторе</th>
                <th className="py-1 pr-2 font-normal" title="Developer-аккаунт в сторе; считается в /stats в чате публикатора">Аккаунт</th>
                <th className="py-1 pr-2 font-normal">Цена</th>
                <th className="py-1 pr-2 font-normal">Листинг</th>
                <th className="py-1 pr-2 font-normal" title="Платим только апрувнутые: автоматически, когда листинг совпал с пакетом, или руками. Апрув оплачивает всё с выхода.">Апрув</th>
                <th className="py-1 font-normal" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a: PublisherApp) => {
                const ls = a.listing_status ? LISTING_LABEL[a.listing_status] : null;
                return (
                  <Fragment key={a.id}>
                    <tr className="border-t border-border align-top">
                      <td className="py-1.5 pr-2">
                        <div className="text-content">{a.name}</div>
                        <div className="font-mono text-xs text-content-subtle">{a.bundle_id}</div>
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {a.client_name ? <span className="text-content">{a.client_name}</span> : <span className="text-content-subtle">наше</span>}
                      </td>
                      <td className="py-1.5 pr-2 text-content-muted whitespace-nowrap">{fmtTs(a.handoff_at ?? a.assigned_at)}</td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {a.is_banned
                          ? <Pill tone="bg-danger/10 text-danger">снято {fmtTs(a.banned_at)}</Pill>
                          : a.published_at
                            ? <Pill tone="bg-success/10 text-success">вышло {fmtTs(a.published_at)}</Pill>
                            : a.released_at
                              ? <Pill tone="bg-success/10 text-success">в сторе с {fmtTs(a.released_at)}</Pill>
                              : <Pill tone="bg-surface-muted text-content-muted">ещё нет</Pill>}
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {(() => {
                          const acc = a.publisher_account || a.store_developer;
                          const label = acc ?? '—';
                          const name = canEdit ? (
                            <button type="button" onClick={() => editAccount(a)} className="text-left text-content-muted hover:text-primary" title={a.publisher_account ? 'Вписано руками' : a.store_developer ? 'Из стора' : 'Неизвестен — вписать'}>
                              {label}{a.publisher_account && <span className="ml-1 text-xs text-content-subtle">(руками)</span>}
                            </button>
                          ) : <span className="text-content-muted">{label}</span>;
                          return (
                            <>
                              {name}
                              {a.store_developer_url && (
                                <a href={a.store_developer_url} target="_blank" rel="noreferrer" className="ml-1 text-xs text-primary hover:underline" title={`Аккаунт в сторе: ${a.store_developer ?? ''}`}>↗</a>
                              )}
                              {a.developers_before.map((d) => (
                                <div key={d.url} className="text-xs text-content-subtle">
                                  раньше:{' '}
                                  <a href={d.url} target="_blank" rel="noreferrer" className="hover:text-primary hover:underline">{d.name}</a>
                                  {d.last_seen_at && <> до {fmtUnixDay(d.last_seen_at)}</>}
                                </div>
                              ))}
                            </>
                          );
                        })()}
                      </td>
                      <td className="py-1.5 pr-2 text-content-muted whitespace-nowrap">
                        {money(a.publish_cents)}{a.rent_cents ? ` + ${money(a.rent_cents)}/нед` : ''}
                        {a.rent_from_week && (
                          <span className="ml-1 text-xs text-content-subtle" title="Ставка аренды с этой недели; раньше — по прежней">с {fmtD(a.rent_from_week)}</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {ls ? <Pill tone={ls.tone}>{ls.label}</Pill> : <span className="text-content-subtle">—</span>}
                        {a.listing_report && (
                          <button type="button" onClick={() => setOpen(open === a.id ? null : a.id)} className="ml-2 text-xs text-primary hover:underline">
                            {open === a.id ? 'скрыть' : 'отчёт'}
                          </button>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {a.approved_at ? (
                          <span className="flex flex-col items-start">
                            <Pill tone="bg-success/10 text-success">
                              {a.approval_source === 'auto' ? 'листинг совпал' : a.approval_source === 'backfill' ? 'до правила' : `${a.approved_by ?? 'команда'}${a.approval_source === 'bot' ? ' · бот' : ''}`}
                            </Pill>
                            {canEdit && a.approval_source !== 'backfill' && (
                              <button
                                type="button"
                                disabled={approval.isPending}
                                onClick={() => { if (window.confirm(`Снять апрув с «${a.name}»? Пока не апрувнут снова, за неё не платим.`)) approval.mutate({ appId: a.id, approve: false }); }}
                                className="text-xs text-content-subtle hover:text-danger disabled:opacity-40"
                              >
                                снять
                              </button>
                            )}
                          </span>
                        ) : (
                          <span className="flex flex-col items-start gap-0.5">
                            <Pill tone="bg-warning/10 text-warning">не платим</Pill>
                            {canEdit && (
                              <button
                                type="button"
                                disabled={approval.isPending}
                                onClick={() => approval.mutate({ appId: a.id, approve: true })}
                                className="text-xs text-primary hover:underline disabled:opacity-40"
                              >
                                апрувнуть
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 text-right whitespace-nowrap">
                        {canEdit && a.released_at && (
                          <button
                            type="button"
                            disabled={check.isPending}
                            onClick={() => check.mutate(a.id)}
                            className="text-xs text-content-muted hover:text-primary disabled:opacity-40"
                          >
                            {check.isPending && check.variables === a.id ? 'проверяем…' : 'проверить сейчас'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {open === a.id && a.listing_report && (
                      <tr className="bg-surface-muted/40">
                        <td colSpan={9} className="px-2 py-2"><ReportView report={a.listing_report} /></td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- week row editor

function WeekRowDialog({ week, row, isCurrent, onClose, onSaved }: {
  week: string; row: PublisherWeekRow; isCurrent: boolean; onClose: () => void; onSaved: () => void;
}) {
  const pushToast = useToastStore((s) => s.push);
  const [status, setStatus] = useState<WeekStatus>(row.status);
  const [paid, setPaid] = useState(usd(row.paid_cents ?? (row.status === 'paid' ? row.total_cents : null)));
  const [note, setNote] = useState(row.note ?? '');
  const save = useMutation({
    mutationFn: () => updatePublisherWeek(week, row.publisher_id, {
      status, paid_cents: paid.trim() === '' ? null : toCents(paid), note: note.trim() || null,
    }),
    onSuccess: () => { pushToast('Сохранено'); onSaved(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось сохранить'),
  });
  return (
    <Modal isOpen onClose={onClose} title={`${row.publisher_name} · ${weekLabel(week)}`} size="2xl">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Публикации" value={`${row.published.filter((p) => !p.void && !p.pending).length} · ${money(row.published_cents)}`} />
          <Stat label="Аренда" value={`${row.rent.filter((r) => !r.pending).length} · ${money(row.rent_cents)}`} />
          <Stat label="Итого" value={money(row.total_cents)} hint={row.pending_cents > 0 ? `ещё ${money(row.pending_cents)} ждут апрува` : undefined} />
        </div>
        {row.published.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wide text-content-subtle mb-1">Вышло за неделю</div>
            <ul className="text-sm space-y-0.5">
              {row.published.map((p) => (
                <li key={p.app_id} className={`flex justify-between gap-2${p.void || p.pending ? ' opacity-60' : ''}`}>
                  <span className="text-content">{p.app_name} <span className="font-mono text-xs text-content-subtle">{p.bundle_id}</span><ClientTag name={p.client_name} /></span>
                  <span className="text-content-muted whitespace-nowrap">
                    {fmtTs(p.at)} · {p.void ? (isCurrent ? 'снята — не платим, если не вернётся к понедельнику' : 'не дожила до понедельника — не платим') : p.pending ? `${money(p.amount_cents)} · ждёт апрува` : money(p.amount_cents)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {row.rent.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wide text-content-subtle mb-1">Аренда: прожитые дни на этой неделе (7 дней = полная ставка)</div>
            <ul className="text-sm space-y-0.5">
              {row.rent.map((r) => (
                <li key={r.app_id} className={`flex justify-between gap-2${r.pending ? ' opacity-60' : ''}`}>
                  <span className="text-content">{r.app_name} <span className="font-mono text-xs text-content-subtle">{r.bundle_id}</span><ClientTag name={r.client_name} /></span>
                  <span className="text-content-muted whitespace-nowrap">
                    {r.days} {days(r.days)}{r.first_week ? ` с выхода ${fmtD(r.released_at)}` : ''} · {money(r.rate_cents)}/нед · {money(r.amount_cents)}{r.pending ? ' · ждёт апрува' : ''}
                  </span>
                </li>
              ))}
            </ul>
            {isCurrent && (
              <div className="mt-1 text-xs text-content-subtle">Аренда за неделю платится только за приложения, которые доживут до понедельника.</div>
            )}
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2 border-t border-border pt-3">
          <div>
            <label className="block text-xs text-content-muted mb-1">Статус</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as WeekStatus)} className={inputCls}>
              {WEEK_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-content-muted mb-1">Оплачено, $</label>
            <input type="number" min="0" value={paid} onChange={(e) => setPaid(e.target.value)} className={inputCls} placeholder={usd(row.total_cents)} />
          </div>
        </div>
        <div>
          <label className="block text-xs text-content-muted mb-1">Заметка</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>Сохранить</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- economics

// Two fixed series (validated categorical slots 1 and 2), stepped per theme.
const SERIES = {
  light: { revenue: '#2a78d6', cost: '#eb6834' },
  dark: { revenue: '#3987e5', cost: '#d95926' },
};

/** "$1.2k" for axes, where the cents do not matter. */
function moneyShort(cents: number): string {
  const v = cents / 100;
  const abs = Math.abs(v);
  const s = abs >= 1000 ? `$${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k` : `$${Math.round(abs)}`;
  return v < 0 ? `-${s}` : s;
}

/** Round-number axis ticks (in cents) covering [min, max] and zero, about five of them. */
function niceTicks(values: number[]): number[] {
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  if (hi === lo) return [0];
  const raw = (hi - lo) / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  const out: number[] = [];
  for (let v = Math.floor(lo / step) * step; v < hi + step; v += step) out.push(v);
  return out;
}

const profitTone = (cents: number) => (cents > 0 ? 'text-success' : cents < 0 ? 'text-danger' : 'text-content-muted');

interface AppTotals {
  app: AppEconomics;
  revenue: number;
  cost: number;
  deps: number;
  /** Cumulative revenue − cost after each week of life (index 0 = release week). */
  cum: number[];
  /** Cumulative revenue after each week of life. */
  cumRevenue: number[];
  /** 1-based week of life in which revenue first covered the cost so far, or null. */
  paybackWeek: number | null;
}

function appTotals(app: AppEconomics): AppTotals {
  let revenue = 0, cost = 0, deps = 0;
  let paybackWeek: number | null = null;
  const cumRevenue: number[] = [];
  const cum = app.weeks.map((w, i) => {
    revenue += w.revenue_cents;
    cost += w.cost_cents;
    deps += w.deps;
    if (paybackWeek == null && revenue > 0 && revenue >= cost) paybackWeek = i + 1;
    cumRevenue.push(revenue);
    return revenue - cost;
  });
  return { app, revenue, cost, deps, cum, cumRevenue, paybackWeek };
}

function ChartTooltipBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2 text-xs shadow-sm">
      <div className="mb-1 font-medium text-content">{title}</div>
      <div className="space-y-0.5 text-content-muted">{children}</div>
    </div>
  );
}

function Swatch({ color, dashed }: { color: string; dashed?: boolean }) {
  return (
    <span
      className="inline-block w-4 align-middle"
      style={{ borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}` }}
    />
  );
}

/**
 * Does a publisher's app pay for itself, and from which week of its life.
 * Revenue is the payout on clicks that came through the app (perf_daily, by
 * click day, as on the Performance page); cost is exactly what the weekly
 * bill charges for it. Dashboard only — the publisher never sees it.
 */
function PublisherEconomicsSection({ publishers, apps, current, loading, publisherId, onPublisher }: {
  publishers: Publisher[];
  apps: AppEconomics[];
  current: string;
  loading: boolean;
  publisherId: string | null;
  onPublisher: (id: string | null) => void;
}) {
  const c = useThemeColors();
  const theme = useThemeStore((s) => s.theme);
  const series = SERIES[theme === 'dark' ? 'dark' : 'light'];
  const [picked, setPicked] = useState<string | null>(null);

  const withApps = publishers.filter((p) => apps.some((a) => a.publisher_id === p.id));
  const hasClients = apps.some((a) => a.client_id);
  const totals = useMemo(
    () => apps.filter((a) => !publisherId || a.publisher_id === publisherId).map(appTotals)
      .sort((a, b) => (b.revenue - b.cost) - (a.revenue - a.cost)),
    [apps, publisherId],
  );
  const sum = totals.reduce((s, t) => ({ revenue: s.revenue + t.revenue, cost: s.cost + t.cost }), { revenue: 0, cost: 0 });
  const paidBack = totals.filter((t) => t.revenue - t.cost >= 0 && t.revenue > 0).length;

  // Calendar weeks: running revenue against running cost for the selection.
  const calendar = useMemo(() => {
    if (!totals.length) return [];
    const byWeek = new Map<string, { revenue: number; cost: number }>();
    for (const t of totals) {
      for (const w of t.app.weeks) {
        const cell = byWeek.get(w.week) ?? { revenue: 0, cost: 0 };
        cell.revenue += w.revenue_cents;
        cell.cost += w.cost_cents;
        byWeek.set(w.week, cell);
      }
    }
    const first = [...byWeek.keys()].sort()[0]!;
    const out: { week: string; revenue: number; cost: number; weekRevenue: number; weekCost: number }[] = [];
    let revenue = 0, cost = 0;
    for (let w = first; w <= current; w = shiftWeek(w, 1)) {
      const cell = byWeek.get(w) ?? { revenue: 0, cost: 0 };
      revenue += cell.revenue;
      cost += cell.cost;
      out.push({ week: w, revenue, cost, weekRevenue: cell.revenue, weekCost: cell.cost });
    }
    return out;
  }, [totals, current]);

  // Weeks of life: every app's running profit, aligned on its release week.
  const life = useMemo(() => {
    const maxWeeks = totals.reduce((m, t) => Math.max(m, t.cum.length), 0);
    const rows: Record<string, number | null>[] = [];
    for (let i = 0; i < maxWeeks; i++) {
      const row: Record<string, number | null> = { n: i + 1 };
      const alive = totals.filter((t) => t.cum.length > i);
      for (const t of totals) row[t.app.app_id] = t.cum.length > i ? t.cum[i]! : null;
      row.avg = alive.length ? Math.round(alive.reduce((s, t) => s + t.cum[i]!, 0) / alive.length) : null;
      row.reached = alive.length;
      row.positive = alive.filter((t) => t.cum[i]! >= 0 && t.cumRevenue[i]! > 0).length;
      rows.push(row);
    }
    return rows;
  }, [totals]);
  const calendarTicks = useMemo(() => niceTicks(calendar.flatMap((d) => [d.revenue, d.cost])), [calendar]);
  const lifeTicks = useMemo(() => niceTicks(totals.flatMap((t) => t.cum)), [totals]);
  const pickedApp = totals.find((t) => t.app.app_id === picked) ?? null;

  const axisProps = { axisLine: false, tickLine: false, tick: { fontSize: 11, fill: c.axis } } as const;

  return (
    <section id="publisher-economics" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-content">Окупаемость</h2>
        <div className="flex flex-wrap gap-1">
          {[{ id: null as string | null, name: 'Все' }, ...withApps].map((p) => (
            <button
              key={p.id ?? 'all'}
              type="button"
              onClick={() => { onPublisher(p.id); setPicked(null); }}
              className={`rounded-full px-3 py-1 text-xs ${publisherId === p.id ? 'bg-primary text-white' : 'bg-surface-muted text-content-muted hover:text-content'}`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-content-subtle">
        Доход — выплаты по конверсиям с кликов через приложение (как на Performance, по дню клика). Расход — ровно то, что
        начисляется публикатору: выход + аренда. Публикатор этого не видит.
      </p>

      {loading && <p className="text-sm text-content-subtle">Загрузка…</p>}
      {!loading && totals.length === 0 && (
        <p className="text-sm text-content-subtle">Пока нет вышедших приложений у публикаторов.</p>
      )}
      {totals.length > 0 && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Stat label="Доход" value={money(sum.revenue)} />
            <Stat label="Заплатили публикатору" value={money(sum.cost)} />
            <Stat label="Профит" value={money(sum.revenue - sum.cost)} />
            <Stat label="Отдача" value={sum.cost ? `×${(sum.revenue / sum.cost).toFixed(1)}` : '—'} hint="доход / расход" />
            <Stat label="Окупились" value={`${paidBack} из ${totals.length}`} hint="приложений в плюсе сейчас" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-border p-3">
              <div className="mb-1 text-sm font-medium text-content">Доход и расход нарастающим итогом</div>
              <div className="mb-2 flex gap-4 text-xs text-content-muted">
                <span><Swatch color={series.revenue} /> доход</span>
                <span><Swatch color={series.cost} /> заплатили публикатору</span>
              </div>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={calendar} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                    <XAxis dataKey="week" {...axisProps} tickFormatter={(w) => fmtD(String(w))} />
                    <YAxis {...axisProps} width={48} ticks={calendarTicks} domain={[calendarTicks[0]!, calendarTicks[calendarTicks.length - 1]!]} tickFormatter={(v) => moneyShort(Number(v))} />
                    <Tooltip
                      content={({ active, payload }) => {
                        const d = active ? payload?.[0]?.payload as (typeof calendar)[number] | undefined : undefined;
                        if (!d) return null;
                        return (
                          <ChartTooltipBox title={`Неделя ${weekLabel(d.week)}`}>
                            <div><Swatch color={series.revenue} /> доход {money(d.revenue)} <span className="text-content-subtle">(+{money(d.weekRevenue)} за неделю)</span></div>
                            <div><Swatch color={series.cost} /> расход {money(d.cost)} <span className="text-content-subtle">(+{money(d.weekCost)})</span></div>
                            <div className="font-medium text-content">профит {money(d.revenue - d.cost)}</div>
                          </ChartTooltipBox>
                        );
                      }}
                    />
                    <Line type="monotone" dataKey="revenue" stroke={series.revenue} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                    <Line type="monotone" dataKey="cost" stroke={series.cost} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-1 text-xs text-content-subtle">Где синяя линия над оранжевой — с этой недели в плюсе.</div>
            </div>

            <div className="rounded-lg border border-border p-3">
              <div className="mb-1 text-sm font-medium text-content">Профит по неделям жизни приложения</div>
              <div className="mb-2 flex flex-wrap gap-4 text-xs text-content-muted">
                <span><Swatch color={series.revenue} /> в среднем на приложение</span>
                <span><Swatch color={c.axis} /> каждое приложение</span>
                {pickedApp && <span><Swatch color={series.cost} /> {pickedApp.app.app_name}</span>}
              </div>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={life} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                    <XAxis dataKey="n" {...axisProps} tickFormatter={(n) => `${n}`} />
                    <YAxis {...axisProps} width={48} ticks={lifeTicks} domain={[lifeTicks[0]!, lifeTicks[lifeTicks.length - 1]!]} tickFormatter={(v) => moneyShort(Number(v))} />
                    <ReferenceLine y={0} stroke={c.axis} />
                    <Tooltip
                      content={({ active, payload }) => {
                        const d = active ? payload?.[0]?.payload as Record<string, number | null> | undefined : undefined;
                        if (!d) return null;
                        const mine = pickedApp ? d[pickedApp.app.app_id] : null;
                        return (
                          <ChartTooltipBox title={`${d.n}-я неделя жизни${d.n === 1 ? ' (неделя выхода)' : ''}`}>
                            <div>дошли до этой недели: {d.reached}, из них в плюсе: {d.positive}</div>
                            <div><Swatch color={series.revenue} /> в среднем {money(d.avg ?? 0)}</div>
                            {pickedApp && mine != null && <div><Swatch color={series.cost} /> {pickedApp.app.app_name}: {money(mine)}</div>}
                          </ChartTooltipBox>
                        );
                      }}
                    />
                    {totals.filter((t) => t.app.app_id !== picked).map((t) => (
                      <Line
                        key={t.app.app_id} type="monotone" dataKey={t.app.app_id} stroke={c.axis} strokeOpacity={0.3}
                        strokeWidth={1} dot={false} activeDot={false} connectNulls={false} isAnimationActive={false}
                      />
                    ))}
                    {pickedApp && (
                      <Line
                        type="monotone" dataKey={pickedApp.app.app_id} stroke={series.cost} strokeWidth={2}
                        dot={false} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false}
                      />
                    )}
                    <Line type="monotone" dataKey="avg" stroke={series.revenue} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-1 text-xs text-content-subtle">
                1 = неделя выхода. Линия пересекает ноль — на этой неделе приложение отбило всё, что за него заплачено.
                Нажмите на строку в таблице ниже, чтобы выделить приложение.
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-content-subtle bg-surface-muted/50">
                  <th className="px-3 py-2 font-normal">Приложение</th>
                  {!publisherId && <th className="px-3 py-2 font-normal">Публикатор</th>}
                  {hasClients && <th className="px-3 py-2 font-normal" title="Доход клиентских приложений — у клиента, не у нас: здесь он всегда 0">Клиент</th>}
                  <th className="px-3 py-2 font-normal">Вышло</th>
                  <th className="px-3 py-2 font-normal text-right">Недель</th>
                  <th className="px-3 py-2 font-normal text-right">Депы</th>
                  <th className="px-3 py-2 font-normal text-right">Доход</th>
                  <th className="px-3 py-2 font-normal text-right">Расход</th>
                  <th className="px-3 py-2 font-normal text-right">Профит</th>
                  <th className="px-3 py-2 font-normal">Окупилось</th>
                </tr>
              </thead>
              <tbody>
                {totals.map((t) => {
                  const profit = t.revenue - t.cost;
                  const pubName = publishers.find((p) => p.id === t.app.publisher_id)?.name ?? '—';
                  return (
                    <tr
                      key={t.app.app_id}
                      onClick={() => setPicked(picked === t.app.app_id ? null : t.app.app_id)}
                      className={`border-t border-border cursor-pointer ${picked === t.app.app_id ? 'bg-primary/5' : 'hover:bg-surface-muted/30'}`}
                    >
                      <td className="px-3 py-2">
                        <div className="text-content">{t.app.app_name}</div>
                        <div className="font-mono text-xs text-content-subtle">{t.app.bundle_id}</div>
                      </td>
                      {!publisherId && <td className="px-3 py-2 text-content-muted">{pubName}</td>}
                      {hasClients && <td className="px-3 py-2 text-content-muted">{t.app.client_name ?? <span className="text-content-subtle">наше</span>}</td>}
                      <td className="px-3 py-2 whitespace-nowrap text-content-muted">
                        {fmtD(t.app.released_at)}
                        {t.app.is_banned && <Pill tone="ml-2 bg-danger/10 text-danger">снято{t.app.banned_at ? ` ${fmtD(t.app.banned_at)}` : ''}</Pill>}
                      </td>
                      <td className="px-3 py-2 text-right text-content-muted">{t.cum.length}</td>
                      <td className="px-3 py-2 text-right text-content-muted">{t.deps}</td>
                      <td className="px-3 py-2 text-right text-content whitespace-nowrap">{money(t.revenue)}</td>
                      <td className="px-3 py-2 text-right text-content-muted whitespace-nowrap">{money(t.cost)}</td>
                      <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${profitTone(profit)}`}>{money(profit)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-content-muted">
                        {t.paybackWeek ? `на ${t.paybackWeek}-й неделе` : <span className="text-content-subtle">ещё нет</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- page

export function Publishers() {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const [week, setWeek] = useState(mondayOf(new Date()));
  const [editing, setEditing] = useState<Publisher | null | 'new'>(null);
  const [appsOf, setAppsOf] = useState<Publisher | null>(null);
  const [weekRow, setWeekRow] = useState<PublisherWeekRow | null>(null);
  const [econOf, setEconOf] = useState<string | null>(null);

  const regQ = useQuery({ queryKey: ['publishers'], queryFn: getPublishers });
  const weekQ = useQuery({ queryKey: ['publisher-week', week], queryFn: () => getPublisherWeek(week) });
  const econQ = useQuery({ queryKey: ['publisher-economics'], queryFn: getPublisherEconomics });

  const publishers = regQ.data?.publishers ?? [];
  const rows = useMemo(
    () => (weekQ.data?.rows ?? []).filter((r) => r.enabled || r.total_cents > 0),
    [weekQ.data],
  );
  const totals = rows.reduce((t, r) => ({
    published: t.published + r.published.filter((p) => !p.void && !p.pending).length, publishedCents: t.publishedCents + r.published_cents,
    rent: t.rent + r.rent.filter((x) => !x.pending).length, rentCents: t.rentCents + r.rent_cents, total: t.total + r.total_cents,
  }), { published: 0, publishedCents: 0, rent: 0, rentCents: 0, total: 0 });
  // The week's revenue per publisher, from the same per-app weeks the payback section uses.
  const revenueBy = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of econQ.data?.apps ?? []) {
      const w = a.weeks.find((x) => x.week === week);
      if (w) m.set(a.publisher_id, (m.get(a.publisher_id) ?? 0) + w.revenue_cents);
    }
    return m;
  }, [econQ.data, week]);
  const weekRevenue = [...revenueBy.values()].reduce((s, v) => s + v, 0);
  const revenueOf = (id: string) => (econQ.data ? revenueBy.get(id) ?? 0 : null);
  const showEconomics = (id: string) => {
    setEconOf(id);
    document.getElementById('publisher-economics')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const isCurrent = weekQ.data ? weekQ.data.week === weekQ.data.current : week === mondayOf(new Date());
  // Only worth a table once some client's app is in the week.
  const clients = weekQ.data?.clients ?? [];
  const showClients = clients.some((g) => g.client_id);

  const refetchWeek = () => qc.invalidateQueries({ queryKey: ['publisher-week', week] });

  return (
    <div className="space-y-5 pb-20">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-semibold text-content">Публикаторы</h1>
          <p className="mt-1 text-sm text-content-subtle">
            Сторонние челы, которые заливают наши приложения в свои консоли. Платим по понедельникам за прошлую неделю: за то, что вышло, и аренду
            за каждое приложение по прожитым дням (ставка / 7 за день). Не дожило до понедельника — аренду за эту неделю не платим; если это неделя выхода — и за выход тоже.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canEdit && <Button size="sm" onClick={() => setEditing('new')}>Добавить публикатора</Button>}
        </div>
      </div>

      {/* Week */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setWeek(shiftWeek(week, -1))}>←</Button>
          <div className="text-sm text-content">
            Неделя <span className="font-semibold">{weekLabel(week)}</span>
            {isCurrent && <span className="ml-2 text-xs text-content-subtle">(текущая, цифры ещё растут)</span>}
          </div>
          <Button variant="secondary" size="sm" onClick={() => setWeek(shiftWeek(week, 1))} disabled={isCurrent}>→</Button>
          {!isCurrent && (
            <button type="button" onClick={() => setWeek(mondayOf(new Date()))} className="text-xs text-primary hover:underline">к текущей</button>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Stat label="Публикации" value={`${totals.published} · ${money(totals.publishedCents)}`} />
          <Stat label="Аренда" value={`${totals.rent} прил. · ${money(totals.rentCents)}`} />
          <Stat label="Итого за неделю" value={money(totals.total)} />
          <Stat label="Доход с их приложений" value={econQ.data ? money(weekRevenue) : '…'} />
          <Stat label="Профит за неделю" value={econQ.data ? money(weekRevenue - totals.total) : '…'} hint="доход − итого" />
        </div>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle bg-surface-muted/50">
                <th className="px-3 py-2 font-normal">Публикатор</th>
                <th className="px-3 py-2 font-normal text-right">Вышло</th>
                <th className="px-3 py-2 font-normal text-right">Аренда</th>
                <th className="px-3 py-2 font-normal text-right">Итого</th>
                <th className="px-3 py-2 font-normal text-right">Доход</th>
                <th className="px-3 py-2 font-normal text-right">Профит</th>
                <th className="px-3 py-2 font-normal">Статус</th>
                <th className="px-3 py-2 font-normal">Оплачено</th>
                <th className="px-3 py-2 font-normal">Заметка</th>
              </tr>
            </thead>
            <tbody>
              {weekQ.isLoading && <tr><td colSpan={9} className="px-3 py-3 text-content-subtle">Загрузка…</td></tr>}
              {!weekQ.isLoading && rows.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-3 text-content-subtle">Публикаторов пока нет.</td></tr>
              )}
              {rows.map((r) => {
                const st = WEEK_STATUS.find((s) => s.value === r.status)!;
                const rev = revenueOf(r.publisher_id);
                return (
                  <tr key={r.publisher_id} className="border-t border-border hover:bg-surface-muted/30 cursor-pointer" onClick={() => setWeekRow(r)}>
                    <td className="px-3 py-2 text-content">{r.publisher_name}</td>
                    <td className="px-3 py-2 text-right text-content-muted whitespace-nowrap">{r.published.filter((p) => !p.void && !p.pending).length} · {money(r.published_cents)}</td>
                    <td className="px-3 py-2 text-right text-content-muted whitespace-nowrap">{r.rent.filter((x) => !x.pending).length} · {money(r.rent_cents)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-content whitespace-nowrap">
                      {money(r.total_cents)}
                      {r.pending_cents > 0 && <div className="text-xs font-normal text-warning" title="Неапрувнутые прилы: добавится после апрува">+{money(r.pending_cents)} ждут апрува</div>}
                    </td>
                    <td className="px-3 py-2 text-right text-content-muted whitespace-nowrap">{rev == null ? '…' : money(rev)}</td>
                    <td className={`px-3 py-2 text-right whitespace-nowrap ${rev == null ? 'text-content-subtle' : profitTone(rev - r.total_cents)}`}>
                      {rev == null ? '…' : money(rev - r.total_cents)}
                    </td>
                    <td className="px-3 py-2"><Pill tone={st.tone}>{st.label}</Pill></td>
                    <td className="px-3 py-2 text-content-muted">{r.paid_cents == null ? '—' : money(r.paid_cents)}</td>
                    <td className="px-3 py-2 text-content-subtle truncate max-w-[16rem]">{r.note ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {showClients && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-content">По клиентам</h3>
            <p className="text-xs text-content-subtle">
              Сколько за неделю начислено публикаторам за приложения каждого внешнего клиента. Клиент этого не видит.
            </p>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-content-subtle bg-surface-muted/50">
                    <th className="px-3 py-2 font-normal">Клиент</th>
                    <th className="px-3 py-2 font-normal text-right">Вышло</th>
                    <th className="px-3 py-2 font-normal text-right">Аренда</th>
                    <th className="px-3 py-2 font-normal text-right">Итого</th>
                    <th className="px-3 py-2 font-normal">Публикаторы</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((g) => (
                    <tr key={g.client_id ?? 'ours'} className="border-t border-border align-top">
                      <td className="px-3 py-2">
                        {g.client_name ? <span className="text-content">{g.client_name}</span> : <span className="text-content-muted">Наши приложения</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-content-muted whitespace-nowrap">{g.published} · {money(g.published_cents)}</td>
                      <td className="px-3 py-2 text-right text-content-muted whitespace-nowrap">{g.rent} · {money(g.rent_cents)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-content whitespace-nowrap">
                        {money(g.total_cents)}
                        {g.pending_cents > 0 && <div className="text-xs font-normal text-warning" title="Неапрувнутые прилы: добавится после апрува">+{money(g.pending_cents)} ждут апрува</div>}
                      </td>
                      <td className="px-3 py-2 text-content-muted">
                        {g.publishers.map((p) => (
                          <div key={p.publisher_id} className="whitespace-nowrap">
                            {p.publisher_name} · {money(p.total_cents)}
                            {p.pending_cents > 0 && <span className="text-xs text-warning"> +{money(p.pending_cents)}</span>}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <PublisherEconomicsSection
        publishers={publishers}
        apps={econQ.data?.apps ?? []}
        current={econQ.data?.current ?? mondayOf(new Date())}
        loading={econQ.isLoading}
        publisherId={econOf}
        onPublisher={setEconOf}
      />

      {/* Registry */}
      <section className="space-y-2">
        <h2 className="text-base font-semibold text-content">Реестр</h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle bg-surface-muted/50">
                <th className="px-3 py-2 font-normal">Публикатор</th>
                <th className="px-3 py-2 font-normal">Чат</th>
                <th className="px-3 py-2 font-normal">Цены по умолчанию</th>
                <th className="px-3 py-2 font-normal text-right">Приложений / живых</th>
                <th className="px-3 py-2 font-normal">Контакт</th>
                <th className="px-3 py-2 font-normal" />
              </tr>
            </thead>
            <tbody>
              {publishers.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-3 text-content-subtle">Добавьте первого публикатора, потом он появится в списке на вкладке Share.</td></tr>
              )}
              {publishers.map((p) => (
                <tr key={p.id} className={`border-t border-border ${p.enabled ? '' : 'opacity-50'}`}>
                  <td className="px-3 py-2 text-content">{p.name}{!p.enabled && <span className="ml-2 text-xs text-content-subtle">выключен</span>}</td>
                  <td className="px-3 py-2 text-content-muted">
                    {p.chat_id ? <span className="font-mono text-xs">{p.chat_id}{p.thread_id != null ? ` / ${p.thread_id}` : ''}</span> : <span className="text-warning text-xs">нет чата, уведомления не уйдут</span>}
                  </td>
                  <td className="px-3 py-2 text-content-muted whitespace-nowrap">{money(p.publish_cents)}{p.rent_cents ? ` + ${money(p.rent_cents)}/нед` : ''}</td>
                  <td className="px-3 py-2 text-right text-content-muted">{p.app_count} / {p.live_count}</td>
                  <td className="px-3 py-2 text-content-muted">{p.contact ?? '—'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button type="button" onClick={() => showEconomics(p.id)} className="text-xs text-primary hover:underline">окупаемость</button>
                    <button type="button" onClick={() => setAppsOf(p)} className="ml-3 text-xs text-primary hover:underline">приложения</button>
                    {canEdit && (
                      <button type="button" onClick={() => setEditing(p)} className="ml-3 text-xs text-content-muted hover:text-primary">изменить</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <PublisherDialog
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['publishers'] }); refetchWeek(); }}
        />
      )}
      {appsOf && <PublisherApps publisher={appsOf} onClose={() => setAppsOf(null)} />}
      {weekRow && (
        <WeekRowDialog
          week={week} row={weekRow} isCurrent={isCurrent}
          onClose={() => setWeekRow(null)}
          onSaved={() => { setWeekRow(null); refetchWeek(); }}
        />
      )}
    </div>
  );
}
